import React, { useState } from 'react'
import {
  Box,
  Fab,
  Zoom,
  Tooltip,
  Stack
} from '@mui/material'
import useRouter from '../../hooks/useRouter'
import BarChartIcon from '@mui/icons-material/BarChart'
import TagIcon from '@mui/icons-material/Tag'
import TikTokIcon from '@mui/icons-material/MusicVideo'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import MenuIcon from '@mui/icons-material/Menu'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'

interface FabOption {
  icon: React.ReactElement
  label: string
  routeName: string
  color: string
}

export const VisualizationsFab: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false)
  const router = useRouter()
  const customer = router.params.customer || ''
  const job = router.params.job || ''

  const options: FabOption[] = [
    {
      icon: <PlayCircleIcon />,
      label: 'TikTok Start',
      routeName: 'tiktok-start',
      color: '#00d4ff'
    },
    {
      icon: <TikTokIcon />,
      label: 'TikTok Analysis',
      routeName: 'tiktok-comments',
      color: '#000000'
    },
    {
      icon: <TrendingUpIcon />,
      label: 'TikTok Overview',
      routeName: 'tiktok-analytics',
      color: '#9c27b0'
    }
  ]

  const handleNavigate = (routeName: string) => {
    router.navigate(routeName, { customer, job })
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1500,
        // Expand the hover area to cover all buttons
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: -10,
          right: -10,
          width: 90,
          height: isHovered ? 220 : 80,  // Adjusted for 3 buttons with 48px spacing
          transition: 'height 0.3s ease',
          pointerEvents: 'auto',
          zIndex: -1,
          // Debug: uncomment to see the hover area
          // backgroundColor: 'rgba(255,0,0,0.1)',
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack
        spacing={1}
        alignItems="center"
        sx={{ position: 'relative' }}
      >
        {/* Options that appear on hover */}
        {options.map((option, index) => (
          <Zoom
            key={option.label}
            in={isHovered}
            timeout={{
              enter: 150 + (index * 30),
              exit: 150 - (index * 30)
            }}
            style={{
              transitionDelay: isHovered ? `${index * 30}ms` : '0ms'
            }}
          >
            <Tooltip title={option.label} placement="left">
              <Fab
                size="medium"
                sx={{
                  bgcolor: option.color,
                  color: 'white',
                  '&:hover': {
                    bgcolor: option.color,
                    filter: 'brightness(0.9)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease',
                  position: 'absolute',
                  bottom: 60 + (index * 48),  // Reduced spacing from 56 to 48
                  boxShadow: 3
                }}
                onClick={() => handleNavigate(option.routeName)}
              >
                {option.icon}
              </Fab>
            </Tooltip>
          </Zoom>
        ))}

        {/* Main FAB button */}
        <Fab
          color="primary"
          aria-label="navigation menu"
          size="large"
          sx={{
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease',
            boxShadow: 4
          }}
        >
          <MenuIcon />
        </Fab>
      </Stack>
    </Box>
  )
}

export default VisualizationsFab